// src/components/TaskDetail.js
import { useState } from "react";
import AIChat from "./AIChat";
import AIRecommendations from "./AIRecommendations";
import { CAT_ICONS, PRIORITY_COLORS, PRIORITY_LIGHT } from "../constants";
import { PartsList } from "./TaskForm";

export default function TaskDetail({ task, onEdit, onDelete, onToggleComplete, onBack, saving, onSaveChat, onUpdateSteps, onAddRecommendedTask }) {
  const [aiTab, setAiTab] = useState("guide");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(false);

  const hasAI = task.repairGuide || (task.partsList && task.partsList.length > 0);
  const images = task.images || [];

  function openLightbox(index) { setLightboxIndex(index); }
  function closeLightbox() { setLightboxIndex(null); }
  function prevImage() { setLightboxIndex(i => (i - 1 + images.length) % images.length); }
  function nextImage() { setLightboxIndex(i => (i + 1) % images.length); }

  function handleKeyDown(e) {
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") prevImage();
    if (e.key === "ArrowRight") nextImage();
  }

  return (
    <div className="fade-in" style={{ maxWidth: 760 }}>
      <button className="btn-secondary" style={{ marginBottom: 20, padding: "8px 16px", fontSize: 13 }} onClick={onBack}>
        Back to Tasks
      </button>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ ...styles.badge, background: PRIORITY_LIGHT[task.priority], color: PRIORITY_COLORS[task.priority] }}>{task.priority}</span>
              <span style={{ ...styles.badge, background: "#f0f4ff", color: "#6366f1" }}>{CAT_ICONS[task.category]} {task.category}</span>
              {task.recurrence && task.recurrence !== "None" && <span style={{ ...styles.badge, background: "#f0fdf4", color: "#22c55e" }}>{task.recurrence}</span>}
              {task.completed && <span style={{ ...styles.badge, background: "#f0fdf4", color: "#22c55e" }}>Completed</span>}
            </div>
            <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 28, color: "#2c1810", lineHeight: 1.2 }}>{task.title}</h2>
            {task.dueDate && <p style={{ fontWeight: 700, color: "#a07850", marginTop: 6, fontSize: 13 }}>Due: {task.dueDate}</p>}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button className="btn-primary" style={{ padding: "9px 16px", fontSize: 13 }} onClick={onEdit}>Edit</button>
            {!confirmDelete
              ? <button className="btn-danger" onClick={() => setConfirmDelete(true)}>Delete</button>
              : <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>Sure?</span>
                  <button className="btn-danger" onClick={onDelete} disabled={saving}>Yes, Delete</button>
                  <button className="btn-secondary" style={{ padding: "8px 12px", fontSize: 13 }} onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
            }
          </div>
        </div>

        <button
          className={task.completed ? "btn-secondary" : "btn-primary"}
          style={{ marginTop: 16, width: "100%", fontSize: 15 }}
          onClick={() => {
            onToggleComplete(task);
            // Show recommendations when marking complete (not when unchecking)
            if (!task.completed) setShowRecommendations(true);
          }}
          disabled={saving}
        >
          {task.completed ? "Mark as Open" : "Mark as Complete"}
        </button>
      </div>

      {task.notes && (
        <div className="card" style={{ marginBottom: 16, background: "#fffaf4" }}>
          <label className="field-label">Notes</label>
          <p style={{ color: "#5c3a1e", fontSize: 14, lineHeight: 1.8, fontWeight: 600, marginTop: 4 }}>{task.notes}</p>
        </div>
      )}

      {images.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <label className="field-label">Photos ({images.length})</label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
            {images.map((img, i) => (
              <div key={i} onClick={() => openLightbox(i)} style={{ position: "relative", cursor: "zoom-in" }}>
                <img src={img} alt="" style={{ width: 130, height: 130, objectFit: "cover", borderRadius: 14, border: "3px solid #f0d8b0", boxShadow: "0 2px 10px rgba(160,100,40,0.15)", transition: "transform 0.18s" }}
                  onMouseEnter={e => e.target.style.transform = "scale(1.05)"}
                  onMouseLeave={e => e.target.style.transform = "scale(1)"}
                />
                <div style={{ position: "absolute", bottom: 6, right: 6, background: "rgba(0,0,0,0.5)", borderRadius: 6, padding: "2px 6px", fontSize: 12, color: "white", fontWeight: 700 }}>🔍</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* YouTube + Contractor Links */}
      <div className="card" style={{ marginBottom: 16, background: "#fafafa", border: "1px solid #e8d5b8" }}>
        <h3 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 18, color: "#8B4513", marginBottom: 14 }}>Need More Help?</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(task.title + " repair how to")}`}
            target="_blank" rel="noopener noreferrer" style={styles.helpBtn}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>▶️</div>
            <div style={{ fontWeight: 800, fontSize: 13, color: "#cc0000" }}>Watch on YouTube</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2, fontWeight: 600 }}>How-to videos for this repair</div>
          </a>
          <a href={(() => {
            const categoryMap = {
              "Plumbing": "plumber", "Electrical": "electrician", "HVAC": "hvac contractor",
              "Roofing": "roofing contractor", "Flooring": "flooring contractor",
              "Structural": "general contractor", "Appliances": "appliance repair",
              "Exterior": "handyman", "Interior": "handyman",
              "Landscaping": "landscaper", "Other": "handyman"
            };
            const service = categoryMap[task.category] || "handyman";
            return `https://www.google.com/search?q=${encodeURIComponent(service + " near me")}&tbm=lcl`;
          })()}
            target="_blank" rel="noopener noreferrer" style={styles.helpBtn}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>🔨</div>
            <div style={{ fontWeight: 800, fontSize: 13, color: "#4285F4" }}>Find a Local Pro</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2, fontWeight: 600 }}>Search local contractors on Google</div>
          </a>
        </div>
      </div>

      {hasAI ? (
        <div className="card" style={{ border: "2px solid #dbeafe", background: "#f8fbff" }}>
          <div style={{ display: "flex", borderBottom: "2px solid #dbeafe", marginBottom: 16 }}>
            <button className={`tab-btn ${aiTab === "guide" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("guide")}>Repair Guide</button>
            <button className={`tab-btn ${aiTab === "parts" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("parts")}>Parts List ({(task.partsList || []).length})</button>
            <button className={`tab-btn ${aiTab === "chat" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("chat")}>Ask AI {task.chatHistory?.length > 0 ? `(${task.chatHistory.length})` : ""}</button>
          </div>
          {aiTab === "guide" && task.repairGuide && (
            <RepairGuideChecklist guide={task.repairGuide} taskId={task.id} completedSteps={task.completedSteps || []} onUpdateSteps={(steps) => onUpdateSteps(task.id, steps)} />
          )}
          {aiTab === "parts" && <PartsList parts={task.partsList || []} />}
          {aiTab === "chat" && <AIChat task={task} onSaveChat={(chatHistory) => onSaveChat(task.id, chatHistory)} />}
        </div>
      ) : (
        <div className="card" style={{ border: "2px solid #dbeafe", background: "#f8fbff" }}>
          <div style={{ display: "flex", borderBottom: "2px solid #dbeafe", marginBottom: 16 }}>
            <button className={`tab-btn ${aiTab === "guide" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("guide")}>Repair Guide</button>
            <button className={`tab-btn ${aiTab === "chat" ? "tab-active-blue" : ""}`} onClick={() => setAiTab("chat")}>Ask AI {task.chatHistory?.length > 0 ? `(${task.chatHistory.length})` : ""}</button>
          </div>
          {aiTab === "guide" && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <p style={{ fontWeight: 700, color: "#6b88b8", marginBottom: 16 }}>No AI guide generated yet.</p>
              <button className="btn-ai" onClick={onEdit}>Edit Task to Generate Guide</button>
            </div>
          )}
          {aiTab === "chat" && <AIChat task={task} onSaveChat={(chatHistory) => onSaveChat(task.id, chatHistory)} />}
        </div>
      )}

      {/* AI Recommendations Modal */}
      {showRecommendations && (
        <AIRecommendations
          task={task}
          onAddTask={onAddRecommendedTask}
          onDismiss={() => setShowRecommendations(false)}
        />
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div style={styles.lightboxBackdrop} onClick={closeLightbox} onKeyDown={handleKeyDown} tabIndex={0} autoFocus>
          <button style={styles.lightboxClose} onClick={closeLightbox}>×</button>
          {images.length > 1 && (
            <button style={{ ...styles.lightboxNav, left: 16 }} onClick={e => { e.stopPropagation(); prevImage(); }}>‹</button>
          )}
          <img src={images[lightboxIndex]} alt="" style={styles.lightboxImage} onClick={e => e.stopPropagation()} />
          {images.length > 1 && (
            <button style={{ ...styles.lightboxNav, right: 16 }} onClick={e => { e.stopPropagation(); nextImage(); }}>›</button>
          )}
          {images.length > 1 && (
            <div style={styles.lightboxCounter}>{lightboxIndex + 1} / {images.length}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Interactive Step Checklist ────────────────────────────────────────────────
function RepairGuideChecklist({ guide, taskId, completedSteps, onUpdateSteps }) {
  const clean = (text) => text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .trim();

  // Parse guide into steps (with descriptions) and other content
  const lines = guide.split("\n");
  const steps = [];
  const headerLines = [];
  let currentStep = null;
  let pastSteps = false;

  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const isStep = /^\d+[\.)]/.test(trimmed);
    if (isStep) {
      pastSteps = true;
      if (currentStep) steps.push(currentStep);
      currentStep = { title: clean(trimmed), description: "" };
    } else if (currentStep) {
      // This line is a description for the current step
      currentStep.description += (currentStep.description ? " " : "") + clean(trimmed);
    } else {
      if (!pastSteps) headerLines.push(clean(trimmed));
    }
  });
  if (currentStep) steps.push(currentStep);

  function toggleStep(index) {
    const updated = completedSteps.includes(index)
      ? completedSteps.filter(i => i !== index)
      : [...completedSteps, index];
    onUpdateSteps(updated);
  }

  const progress = steps.length > 0 ? Math.round((completedSteps.length / steps.length) * 100) : 0;

  return (
    <div>
      {/* Header content (disclaimer etc) */}
      {headerLines.map((line, i) => (
        line && <p key={`h${i}`} style={{
          fontSize: 12, color: "#64748b", lineHeight: 1.7, fontWeight: 600,
          marginBottom: 8,
          background: line.includes("DISCLAIMER") || line.includes("IMPORTANT") ? "#fff7ed" : "transparent",
          padding: line.includes("DISCLAIMER") || line.includes("IMPORTANT") ? "8px 12px" : 0,
          borderRadius: 8,
          borderLeft: line.includes("DISCLAIMER") || line.includes("IMPORTANT") ? "3px solid #f97316" : "none",
        }}>{line}</p>
      ))}

      {/* Progress bar */}
      {steps.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 800, color: "#6b88b8", marginBottom: 6 }}>
            <span>Progress</span>
            <span>{completedSteps.length}/{steps.length} steps complete</span>
          </div>
          <div style={{ background: "#dbeafe", borderRadius: 8, height: 10 }}>
            <div style={{ background: progress === 100 ? "#22c55e" : "linear-gradient(90deg, #3b82f6, #6366f1)", height: 10, borderRadius: 8, width: `${progress}%`, transition: "width 0.4s ease" }} />
          </div>
          {progress === 100 && (
            <div style={{ textAlign: "center", marginTop: 8, color: "#22c55e", fontWeight: 800, fontSize: 13 }}>
              All steps complete! 🎉
            </div>
          )}
        </div>
      )}

      {/* Clickable steps */}
      <div style={{ marginTop: 8 }}>
        {steps.map((step, i) => {
          const done = completedSteps.includes(i);
          return (
            <div key={i}
              onClick={() => toggleStep(i)}
              style={{
                display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px",
                borderRadius: 12, marginBottom: 8, cursor: "pointer",
                background: done ? "#f0fdf4" : "white",
                border: `2px solid ${done ? "#86efac" : "#dbeafe"}`,
                transition: "all 0.2s",
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                border: `2.5px solid ${done ? "#22c55e" : "#93c5fd"}`,
                background: done ? "#22c55e" : "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.2s",
              }}>
                {done && <span style={{ color: "white", fontSize: 13, fontWeight: 900 }}>✓</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: done ? "#15803d" : "#1e3a5f", textDecoration: done ? "line-through" : "none", lineHeight: 1.5 }}>
                  {step.title}
                </div>
                {step.description && !done && (
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginTop: 4, lineHeight: 1.6 }}>
                    {step.description}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── AI Task Recommendations ───────────────────────────────────────────────────

const styles = {
  badge: { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontFamily: "'Nunito', sans-serif", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" },
  helpBtn: {
    flex: 1, minWidth: 140, background: "white", border: "2px solid #e8d5b8",
    borderRadius: 14, padding: "14px 16px", textAlign: "center",
    textDecoration: "none", display: "block", transition: "all 0.18s",
    cursor: "pointer", boxShadow: "0 2px 8px rgba(160,100,40,0.08)",
  },
  lightboxBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out", animation: "fadeIn 0.2s ease" },
  lightboxImage: { maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12, boxShadow: "0 8px 40px rgba(0,0,0,0.6)", cursor: "default" },
  lightboxClose: { position: "fixed", top: 16, right: 20, background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", color: "white", width: 44, height: 44, borderRadius: "50%", fontSize: 24, fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 },
  lightboxNav: { position: "fixed", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.3)", color: "white", width: 48, height: 48, borderRadius: "50%", fontSize: 28, fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  lightboxCounter: { position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.6)", color: "white", padding: "6px 16px", borderRadius: 20, fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 700 },
};
